// SPDX-License-Identifier: AGPL-3.0-only

import {
  OpenFloodLayer,
  loadDemDataset,
  type DemDatasetManifest,
  type LoadedDemDataset
} from '@openfloodlab/maplibre-layer';
import {
  CpuShallowWaterSolver,
  type InflowSource,
  type SimulationSnapshot,
  type SimulationStatistics,
  type SolverParameters
} from '@openfloodlab/simulation-core';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
import { onBeforeUnmount, onMounted, reactive, ref, shallowRef } from 'vue';

export const DATASETS = [
  {
    id: 'boulder-canyon-3dep',
    manifestUrl: `${import.meta.env.BASE_URL}data/boulder-canyon-3dep.json`
  },
  { id: 'synthetic-basin', manifestUrl: `${import.meta.env.BASE_URL}data/synthetic-basin.json` }
] as const;

const EMPTY_STATISTICS: SimulationStatistics = {
  elapsedSeconds: 0,
  iteration: 0,
  wetCellCount: 0,
  maximumDepthMeters: 0,
  meanWetDepthMeters: 0,
  waterVolumeCubicMeters: 0
};

const SOLVER_PARAMETERS = {} satisfies Partial<SolverParameters>;
const SOFTWARE = { name: 'OpenFloodLab', version: '0.1.0' } as const;

export function useFloodMap() {
  const container = ref<HTMLElement | null>(null);
  const map = shallowRef<MapLibreMap | null>(null);
  const layer = shallowRef<OpenFloodLayer | null>(null);
  const dataset = shallowRef<LoadedDemDataset | null>(null);
  const sources = ref<InflowSource[]>([]);
  const statistics = ref<SimulationStatistics>({ ...EMPTY_STATISTICS });
  const clickPlacementEnabled = ref(false);
  const sourceRadiusMeters = ref(90);
  const sourceFlowRate = ref(25_000);
  const status = reactive({
    loading: true,
    ready: false,
    paused: false,
    webgl2: false,
    floatFramebuffer: false,
    error: ''
  });
  let statisticsTimer: number | undefined;
  let selectedDatasetId = DATASETS[0].id as string;
  let initializationGeneration = 0;
  let initializationController: AbortController | null = null;

  async function initialize(datasetId = selectedDatasetId): Promise<void> {
    const generation = ++initializationGeneration;
    initializationController?.abort();
    const controller = new AbortController();
    initializationController = controller;
    selectedDatasetId = datasetId;
    status.loading = true;
    status.ready = false;
    status.paused = false;
    status.webgl2 = false;
    status.floatFramebuffer = false;
    status.error = '';
    setClickPlacement(false);
    disposeMap();
    if (!container.value) {
      status.loading = false;
      initializationController = null;
      return;
    }

    let instance: MapLibreMap | null = null;
    try {
      const descriptor = DATASETS.find((item) => item.id === datasetId) ?? DATASETS[0];
      const loaded = await loadDemDataset(descriptor.manifestUrl, { signal: controller.signal });
      if (!isCurrentInitialization(generation, controller)) return;
      dataset.value = loaded;
      sourceRadiusMeters.value = loaded.manifest.suggestedSource.radiusMeters;
      sourceFlowRate.value = loaded.manifest.suggestedSource.flowRateCubicMetersPerSecond;

      instance = new maplibregl.Map({
        container: container.value,
        style: createMapStyle(),
        center: [...loaded.manifest.suggestedView.center] as [number, number],
        zoom: loaded.manifest.suggestedView.zoom,
        pitch: loaded.manifest.suggestedView.pitch,
        bearing: loaded.manifest.suggestedView.bearing,
        canvasContextAttributes: { antialias: true, contextType: 'webgl2' },
        renderWorldCopies: false,
        attributionControl: { compact: true }
      });
      map.value = instance;
      instance.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
      instance.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-right');
      await waitForMapLoad(instance, controller.signal);
      if (!isCurrentInitialization(generation, controller)) return;

      const simulationLayer = new OpenFloodLayer({
        grid: loaded.grid,
        minimumElevationMeters: loaded.manifest.minimumElevationMeters,
        maximumElevationMeters: loaded.manifest.maximumElevationMeters,
        simulationSpeed: 10,
        waterThresholdMeters: 0.01,
        waterHeightScale: 1.6,
        solverParameters: SOLVER_PARAMETERS,
        reportContext: {
          dem: {
            encoding: loaded.manifest.encoding,
            sha256: loaded.manifest.sha256,
            source: loaded.manifest.source
          },
          software: SOFTWARE
        }
      });
      layer.value = simulationLayer;
      instance.addLayer(simulationLayer);
      instance.on('click', (event) => {
        if (!clickPlacementEnabled.value) return;
        const position = simulationLayer.pickDemPosition(event.point.x, event.point.y);
        if (!position) {
          status.error = 'Click directly on the loaded DEM surface to add an inflow.';
          return;
        }
        addSource(position[0], position[1]);
      });

      const canvas = instance.getCanvas();
      const context = canvas.getContext('webgl2');
      status.webgl2 = !!context;
      status.floatFramebuffer = !!context?.getExtension('EXT_color_buffer_float');
      status.ready = true;
      status.loading = false;
      installTestHooks();
      statisticsTimer = window.setInterval(refreshStatistics, 750);
    } catch (error) {
      if (isAbortError(error) || !isCurrentInitialization(generation, controller)) return;
      if (instance && map.value === instance) {
        instance.remove();
        map.value = null;
        layer.value = null;
      }
      status.error = error instanceof Error ? error.message : String(error);
      status.loading = false;
      status.ready = false;
    } finally {
      if (isCurrentInitialization(generation, controller)) initializationController = null;
    }
  }

  function isCurrentInitialization(generation: number, controller: AbortController): boolean {
    return initializationGeneration === generation && initializationController === controller;
  }

  function addDefaultSource(): void {
    const suggested = requireManifest().suggestedSource;
    addSource(suggested.longitude, suggested.latitude);
  }

  function addSource(longitude: number, latitude: number): void {
    const manifest = requireManifest();
    if (!pointInBounds(longitude, latitude, manifest.bounds)) {
      status.error = 'The selected point is outside the loaded DEM.';
      return;
    }
    const source: InflowSource = {
      id: `inflow-${Date.now()}-${sources.value.length + 1}`,
      longitude,
      latitude,
      radiusMeters: sourceRadiusMeters.value,
      flowRateCubicMetersPerSecond: sourceFlowRate.value
    };
    sources.value = [...sources.value, source];
    layer.value?.setSources(sources.value);
    status.error = '';
  }

  function clearSources(): void {
    sources.value = [];
    layer.value?.setSources([]);
  }

  function updateSource(
    id: string,
    changes: Partial<Pick<InflowSource, 'radiusMeters' | 'flowRateCubicMetersPerSecond'>>
  ): void {
    const current = sources.value.find((source) => source.id === id);
    if (!current) return;
    const updated: InflowSource = {
      ...current,
      radiusMeters:
        changes.radiusMeters === undefined
          ? current.radiusMeters
          : clamp(changes.radiusMeters, 20, 600),
      flowRateCubicMetersPerSecond:
        changes.flowRateCubicMetersPerSecond === undefined
          ? current.flowRateCubicMetersPerSecond
          : clamp(changes.flowRateCubicMetersPerSecond, 0, 50_000)
    };
    sources.value = sources.value.map((source) => (source.id === id ? updated : source));
    layer.value?.setSources(sources.value);
  }

  function removeSource(id: string): void {
    if (!sources.value.some((source) => source.id === id)) return;
    sources.value = sources.value.filter((source) => source.id !== id);
    layer.value?.setSources(sources.value);
  }

  function toggleClickPlacement(): void {
    setClickPlacement(!clickPlacementEnabled.value);
  }

  function setClickPlacement(enabled: boolean): void {
    clickPlacementEnabled.value = enabled;
    const canvas = map.value?.getCanvas();
    if (canvas) canvas.style.cursor = enabled ? 'crosshair' : '';
  }

  function reset(): void {
    layer.value?.reset();
    statistics.value = { ...EMPTY_STATISTICS };
  }

  function togglePause(): void {
    setPaused(!status.paused);
  }

  function setPaused(paused: boolean): void {
    status.paused = paused;
    layer.value?.setPaused(paused);
  }

  function setSpeed(multiplier: number): void {
    layer.value?.setSimulationSpeed(multiplier);
  }

  function setThreshold(thresholdMeters: number): void {
    layer.value?.setWaterThresholdMeters(thresholdMeters);
  }

  function setWaterHeightScale(scale: number): void {
    layer.value?.setWaterHeightScale(scale);
  }

  function exportResult(type: 'depth' | 'extent' | 'report', thresholdMeters: number): void {
    if (!layer.value) return;
    const value =
      type === 'depth'
        ? layer.value.exportDepth(thresholdMeters)
        : type === 'extent'
          ? layer.value.exportExtent(thresholdMeters)
          : layer.value.exportReport(thresholdMeters);
    downloadJson(value, `${selectedDatasetId}-${type}.json`);
  }

  function refreshStatistics(): void {
    if (!layer.value || !status.ready) return;
    try {
      statistics.value = layer.value.snapshot().statistics;
    } catch (error) {
      status.error = error instanceof Error ? error.message : String(error);
    }
  }

  function disposeMap(): void {
    if (statisticsTimer) window.clearInterval(statisticsTimer);
    statisticsTimer = undefined;
    sources.value = [];
    statistics.value = { ...EMPTY_STATISTICS };
    clickPlacementEnabled.value = false;
    dataset.value = null;
    layer.value = null;
    map.value?.remove();
    map.value = null;
  }

  function requireManifest(): DemDatasetManifest {
    if (!dataset.value) throw new Error('DEM dataset is not loaded.');
    return dataset.value.manifest;
  }

  function installTestHooks(): void {
    window.__OPENFLOODLAB__ = {
      ready: () => status.ready,
      getStatus: () => ({
        ...status,
        statistics: statistics.value,
        sources: sources.value,
        activeDomain: layer.value?.getActiveDomain() ?? null,
        markerOffsets: sources.value.map((source) => {
          const [x, y] = layer.value?.getTerrainScreenOffset(source.longitude, source.latitude) ?? [
            0, 0
          ];
          return { x, y };
        })
      }),
      setPitch: (pitch: number) => map.value?.setPitch(pitch),
      addDefaultSource,
      updateSource,
      removeSource,
      clearSources,
      reset,
      pause: () => setPaused(true),
      resume: () => setPaused(false),
      advance: (iterations = 1, timeStepSeconds?: number) =>
        layer.value?.advance(iterations, timeStepSeconds),
      getSnapshotSummary: () => snapshotSummary(requireLayer().snapshot()),
      exportDepth: (thresholdMeters = 0.01) => requireLayer().exportDepth(thresholdMeters),
      exportExtent: (thresholdMeters = 0.01) => requireLayer().exportExtent(thresholdMeters),
      exportReport: (thresholdMeters = 0.01) => requireLayer().exportReport(thresholdMeters),
      runCpuGpuParity
    };
  }

  function runCpuGpuParity(iterations = 24, timeStepSeconds = 0.05) {
    const simulationLayer = requireLayer();
    const loaded = dataset.value;
    if (!loaded) throw new Error('DEM dataset is not loaded.');
    if (!Number.isInteger(iterations) || iterations < 1 || iterations > 200) {
      throw new Error('Parity iterations must be an integer between 1 and 200.');
    }
    if (!(timeStepSeconds > 0) || timeStepSeconds > 1) {
      throw new Error('Parity time step must be between 0 and 1 second.');
    }

    setPaused(true);
    clearSources();
    const suggested = loaded.manifest.suggestedSource;
    const source: InflowSource = {
      id: 'cpu-gpu-parity-source',
      longitude: suggested.longitude,
      latitude: suggested.latitude,
      radiusMeters: suggested.radiusMeters,
      flowRateCubicMetersPerSecond: suggested.flowRateCubicMetersPerSecond
    };
    sources.value = [source];
    simulationLayer.setSources(sources.value);
    simulationLayer.reset();

    const cpu = new CpuShallowWaterSolver(loaded.grid, SOLVER_PARAMETERS);
    cpu.setSources([source]);
    for (let index = 0; index < iterations; index += 1) {
      simulationLayer.advance(1, timeStepSeconds);
      cpu.step(timeStepSeconds);
    }

    const gpuSnapshot = simulationLayer.snapshot();
    const cpuSnapshot = cpu.snapshot();
    let absoluteDepthError = 0;
    let maximumDepthError = 0;
    for (let index = 0; index < gpuSnapshot.depth.length; index += 1) {
      const error = Math.abs((gpuSnapshot.depth[index] ?? 0) - (cpuSnapshot.depth[index] ?? 0));
      absoluteDepthError += error;
      maximumDepthError = Math.max(maximumDepthError, error);
    }
    const cpuVolume = cpuSnapshot.statistics.waterVolumeCubicMeters;
    return {
      iterations,
      timeStepSeconds,
      meanAbsoluteDepthError: absoluteDepthError / gpuSnapshot.depth.length,
      maximumDepthError,
      volumeRelativeError:
        Math.abs(gpuSnapshot.statistics.waterVolumeCubicMeters - cpuVolume) /
        Math.max(cpuVolume, 1e-9),
      gpu: gpuSnapshot.statistics,
      cpu: cpuSnapshot.statistics,
      gpuDomain: simulationLayer.getActiveDomain(),
      cpuDomain: cpu.activeDomain
    };
  }

  function requireLayer(): OpenFloodLayer {
    if (!layer.value) throw new Error('Simulation layer is not ready.');
    return layer.value;
  }

  onBeforeUnmount(() => {
    initializationGeneration += 1;
    initializationController?.abort();
    initializationController = null;
    disposeMap();
    window.removeEventListener('keydown', handlePlacementKeydown);
    delete window.__OPENFLOODLAB__;
  });

  onMounted(() => window.addEventListener('keydown', handlePlacementKeydown));

  function handlePlacementKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && clickPlacementEnabled.value) setClickPlacement(false);
  }

  return {
    container,
    dataset,
    sources,
    statistics,
    status,
    clickPlacementEnabled,
    sourceRadiusMeters,
    sourceFlowRate,
    initialize,
    addDefaultSource,
    toggleClickPlacement,
    updateSource,
    removeSource,
    clearSources,
    reset,
    togglePause,
    setSpeed,
    setThreshold,
    setWaterHeightScale,
    exportResult
  };
}

function snapshotSummary(snapshot: SimulationSnapshot) {
  return snapshot.statistics;
}

function createMapStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution: '© OpenStreetMap contributors'
      }
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#dbe3dc' } },
      {
        id: 'osm',
        type: 'raster',
        source: 'osm',
        paint: { 'raster-opacity': 0.68, 'raster-saturation': -0.45, 'raster-contrast': 0.08 }
      }
    ]
  };
}

function waitForMapLoad(map: MapLibreMap, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      map.off('load', onLoad);
      signal.removeEventListener('abort', onAbort);
      if (error) reject(error);
      else resolve();
    };
    const onLoad = () => finish();
    const onAbort = () => finish(new DOMException('Map load aborted.', 'AbortError'));
    const timeout = window.setTimeout(() => finish(new Error('Map load timed out.')), 20_000);
    map.on('load', onLoad);
    if (signal.aborted) onAbort();
    else signal.addEventListener('abort', onAbort, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  );
}

function pointInBounds(longitude: number, latitude: number, bounds: readonly number[]): boolean {
  return (
    longitude >= (bounds[0] ?? 0) &&
    longitude <= (bounds[2] ?? 0) &&
    latitude >= (bounds[1] ?? 0) &&
    latitude <= (bounds[3] ?? 0)
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  const finiteValue = Number.isFinite(value) ? value : minimum;
  return Math.max(minimum, Math.min(maximum, finiteValue));
}

function downloadJson(value: unknown, filename: string): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
